import { defineStore } from 'pinia'
import { errorMessage } from '@/services/http'
import { filesService } from '@/services/files'
import { watermarkRejection } from '@/constants/formats'
import { FILE_CATEGORY } from '@/types/api'
import type { SelectOption } from '@/types/ui'
import type { UserFile } from '@/types/api'

export const NO_WATERMARK = ''

interface WatermarksState {
  items: UserFile[]
  loaded: boolean
  loading: boolean
  uploading: boolean
  error: string | null
}

export const useWatermarksStore = defineStore('watermarks', {
  state: (): WatermarksState => ({
    items: [],
    loaded: false,
    loading: false,
    uploading: false,
    error: null,
  }),

  getters: {
    options: (state): SelectOption[] => [
      { value: NO_WATERMARK, label: 'No Watermark' },
      ...state.items.map((file) => ({
        value: file.id,
        label: file.filename,
        image: filesService.rawUrl(file.id),
      })),
    ],
  },

  actions: {
    async refresh(): Promise<void> {
      if (this.loading) return
      this.loading = true
      try {
        this.items = await filesService.list(FILE_CATEGORY.watermark)
        this.loaded = true
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }
    },

    async ensureLoaded(): Promise<void> {
      if (this.loaded) return
      await this.refresh()
    },

    async upload(file: File): Promise<string | null> {
      this.error = null

      const rejection = watermarkRejection(file)
      if (rejection) {
        this.error = rejection
        return null
      }

      this.uploading = true
      try {
        const uploaded = await filesService.uploadWatermark(file)
        this.items = [uploaded, ...this.items.filter((f) => f.id !== uploaded.id)]
        return uploaded.id
      } catch (e) {
        this.error = errorMessage(e)
        return null
      } finally {
        this.uploading = false
      }
    },
  },
})
